/**
 * Chat message blocks — the assistant's reusable rich-response surface.
 *
 * A "block" is structured UI attached to an assistant message: a channel picker,
 * an import-progress card, a confirmation panel. It is the generic form of what
 * `CommandPlanCard` and `ReportPreviewCard` do today as one-off fields on
 * `AIChatMessage` — except a block is addressed by a STRING TYPE and rendered
 * through this registry, so a new one costs a renderer registration instead of a
 * new field plus a new branch in the message component.
 *
 * ─── Why a registry and not a direct import ─────────────────────────────────
 * The renderers need domain stores (`integrations`, `warehouses`, …) and live in
 * `components/application/`. `domains/aiAssistant` must not import from those —
 * that inverts the dependency direction. So this module holds only the CONTRACT
 * (a string type → a React renderer) and the consumers register into it at
 * module load. Same shape and same reasoning as `prefillRegistry.ts`, which
 * documents the identical problem for prefill commands.
 *
 * ─── Unknown types are ignored, not thrown ──────────────────────────────────
 * Unlike the prefill registry — where an unsupported field means a plan step
 * would silently no-op, so it throws — a block is decoration on a message that
 * already carries its own text. A backend that emits a block this client build
 * does not know about should degrade to "just the message", never to a blank
 * chat. The drift test asserts every type the app EMITS has a renderer; it does
 * not require the reverse.
 */

import type { ComponentType } from 'react';

/** The payload shape is per-block; each renderer narrows its own. */
export type MessageBlock = {
  type: string;
  payload: unknown;
};

export type MessageBlockRendererProps = {
  /** The block as it arrived on the message. */
  block: MessageBlock;
  /** The message the block is attached to, so a renderer can key state by it. */
  messageId: string;
};

export type MessageBlockRenderer = ComponentType<MessageBlockRendererProps>;

const registry = new Map<string, MessageBlockRenderer>();

/**
 * Register a renderer for a block type. Idempotent by design: React Fast Refresh
 * re-executes module bodies, and a `console.warn` on every hot reload trains
 * people to ignore warnings. A genuine duplicate registration across two
 * different modules is caught by the drift test, not at runtime.
 */
export const registerMessageBlock = (
  type: string,
  renderer: MessageBlockRenderer
): void => {
  registry.set( type, renderer );
};

/** `undefined` for an unknown type — the caller renders nothing extra. */
export const getMessageBlockRenderer = (
  type: string
): MessageBlockRenderer | undefined => registry.get( type );

/** Registered types, for tests and introspection. */
export const registeredMessageBlockTypes = (): string[] => [...registry.keys()];

/**
 * Block types this app emits itself (as opposed to ones the backend may send).
 * The drift test asserts each has a renderer registered, so renaming a type in
 * one place and not the other fails a test instead of silently rendering a bare
 * message.
 */
export const OWN_MESSAGE_BLOCK_TYPES = [
  'channel-picker',
  'import-progress',
  'rule-preview',
  'automation-draft'
] as const;

export type OwnMessageBlockType = typeof OWN_MESSAGE_BLOCK_TYPES[number];
